// interactions/dipoles.js
/**
 * @fileoverview Dipole moment computation for atom groups.
 * Based on the Tcl DipoleMoment and SetDipoleMonitor implementations.
 *
 * DipoleMoment for a set of atoms:
 *   dipole = Σ(charge_i × position_i) - netCharge × centerOfMass
 *
 * Origin-independent when net charge is zero.
 * Negligible if magnitude < 0.001.
 *
 * For protein amino-acid residues:
 *   - Side-chain dipole per residue (CA, HA + non-backbone atoms)
 *   - Peptide-bond amide dipole across consecutive residue pairs:
 *     C(n), O(n), N(n+1), H(n+1), centered at midpoint of
 *     C(n) and N(n+1).
 * DNA/RNA residues are not split; a single whole-residue dipole
 * is computed instead.
 */
import {
    Length3, Add3_3, Sub3_3, Mul3_1, Div3_1, Normalize3,
} from 'BMapsSrc/math';
import { AtomGroupTypes } from 'BMapsSrc/model/atomgroups';
import { BfdMolTypeCodes } from 'BMapsSrc/molecule_types';

const ATOMIC_WEIGHTS = {
    H: 1.008,
    C: 12.011,
    N: 14.007,
    O: 15.999,
    S: 32.065,
    P: 30.974,
    F: 18.998,
    Cl: 35.453,
    Br: 79.904,
    I: 126.904,
    Se: 78.971,
    Si: 28.086,
    B: 10.81,
};

function getAtomicWeight(atom) {
    return ATOMIC_WEIGHTS[atom.elem] || 12;
}

export const defaultDipoleParams = {
    strongThreshold: 3.0, // Debye
    moderateThreshold: 1.0, // Debye
    threshold: 0.3, // Debye, minimum to display
};

// Sidechain atom names that are technically backbone but belong
// to the sidechain dipole: CA and its attached hydrogens.
const SIDECHAIN_BACKBONE = new Set(['CA', 'HA', 'HA1', 'HA2', 'HA3']);

// Amide bond atom names: the peptide bond N-H...C=O
const AMIDE_NAMES = new Set(['N', 'H', 'C', 'O']);

// All backbone atom names (amide + HA variants). Anything not in
// this set and not in SIDECHAIN_BACKBONE is a sidechain atom.
const BACKBONE_NAMES = new Set([...AMIDE_NAMES, 'HA', 'HA1', 'HA2', 'HA3']);

// DNA/RNA molType codes — these residues should NOT be split
// into sidechain/amide.
const NUCLEIC_ACID_TYPES = new Set([
    BfdMolTypeCodes.ref_dna,
    BfdMolTypeCodes.ref_rna,
    BfdMolTypeCodes.micro_dna,
    BfdMolTypeCodes.micro_rna,
]);

/**
 * Compute dipole moment for a set of atoms.
 * @param {import('BMapsModel').Atom[]} atoms
 * @returns {?{
 *     dipole: number[], magnitude: number,
 *     direction: number[], centerOfMass: number[]
 * }}
 */
export function computeDipoleMoment(atoms) {
    if (atoms.length < 2) return null;

    let dipole = [0, 0, 0];
    let netCharge = 0;
    let totalMass = 0;
    let com = [0, 0, 0];

    for (const atom of atoms) {
        const pos = atom.getPosition();
        if (!pos || Number.isNaN(pos[0] + pos[1] + pos[2])) {
            console.warn('Dipole: bad position for',
                atom.atom, atom.elem, pos);
            continue;
        }
        const chg = Number(atom.charge) || 0;
        const wt = getAtomicWeight(atom);
        netCharge += chg;
        dipole = Add3_3(dipole, Mul3_1(pos, chg));
        totalMass += wt;
        com = Add3_3(com, Mul3_1(pos, wt));
    }

    com = Div3_1(com, totalMass);
    dipole = Sub3_3(dipole, Mul3_1(com, netCharge));
    const magnitude = Length3(dipole);
    if (magnitude < 0.001) return null;

    return {
        dipole,
        magnitude,
        direction: Normalize3(dipole),
        centerOfMass: com,
    };
}

/**
 * Compute geometric center (simple average of valid positions).
 * Skips atoms with missing or NaN coordinates.
 * @param {import('BMapsModel').Atom[]} atoms
 * @returns {?number[]} null if no atoms have valid positions
 */
function computeGeometricCenter(atoms) {
    let center = [0, 0, 0];
    let count = 0;
    for (const atom of atoms) {
        const pos = atom.getPosition();
        if (!pos || Number.isNaN(pos[0] + pos[1] + pos[2])) continue;
        center = Add3_3(center, pos);
        count++;
    }
    if (count === 0) return null;
    return Div3_1(center, count);
}

/**
 * Test whether an atom group is a protein amino-acid residue
 * (not DNA/RNA, saccharide, etc.).
 */
function isProteinResidue(group) {
    return group.type === AtomGroupTypes.Residue
        && !NUCLEIC_ACID_TYPES.has(group.molType);
}

/**
 * Collect sidechain atoms from a protein residue.
 * Sidechain: CA, HA (HA1/HA2/HA3 for GLY) + all non-backbone atoms.
 */
function getSidechainAtoms(group) {
    const sidechain = [];
    for (const atom of group.getAtoms()) {
        const name = atom.atom;
        if (SIDECHAIN_BACKBONE.has(name)
            || !BACKBONE_NAMES.has(name)) {
            sidechain.push(atom);
        }
    }
    return sidechain;
}

/**
 * Find a specific atom by name in a residue's atom list.
 */
function findAtom(atoms, name) {
    return atoms.find((a) => a.atom === name) || null;
}

/**
 * Find dipoles for a list of atom groups.
 *
 * For protein amino-acid residues, produces:
 *   1. Side-chain dipole (CA, HA + non-backbone atoms) per residue
 *   2. Peptide-bond amide dipole from C(n),O(n),N(n+1),H(n+1)
 *      for each pair of consecutive residues, centered at the
 *      midpoint of C(n) and N(n+1).
 *
 * For compounds, cofactors, and DNA/RNA residues, produces
 * one dipole for all atoms.
 *
 * @param {import('BMapsModel').AtomGroup[]} atomGroups
 * @param {object} params
 * @returns {Array<{
 *     group: AtomGroup, atoms: Atom[],
 *     label: string,
 *     dipole: number[], magnitude: number,
 *     direction: number[], centerOfMass: number[]
 * }>}
 */
export function findDipoles(
    atomGroups, params = defaultDipoleParams
) {
    const results = [];
    const proteinResidues = [];

    for (const group of atomGroups) {
        if (isProteinResidue(group)) {
            proteinResidues.push(group);
            const sidechain = getSidechainAtoms(group);
            const geomCenter = computeGeometricCenter(sidechain);
            addDipole(
                results, group, sidechain,
                'sidechain', params, geomCenter
            );
        } else {
            const atoms = group.getAtoms();
            addDipole(results, group, atoms, null, params);
        }
    }

    // Amide dipoles from consecutive residue pairs
    addAmideDipoles(results, proteinResidues, params);

    return results;
}

/**
 * Compute amide-bond dipoles between consecutive residues.
 * The peptide bond C(n)=O(n)...N(n+1)-H(n+1) spans two
 * residues. The dipole arrow is centered at the midpoint
 * of C(n) and N(n+1).
 */
function addAmideDipoles(results, residues, params) {
    if (residues.length < 2) return;

    // Group by chain, sort by residue number
    const byChain = new Map();
    for (const res of residues) {
        const ch = res.chain;
        if (!byChain.has(ch)) byChain.set(ch, []);
        byChain.get(ch).push(res);
    }

    for (const chainRes of byChain.values()) {
        chainRes.sort((a, b) => a.resi - b.resi);
        for (let i = 0; i < chainRes.length - 1; i++) {
            const res1 = chainRes[i];
            const res2 = chainRes[i + 1];
            // Only consecutive residues
            if (res2.resi - res1.resi !== 1) continue;

            const atoms1 = res1.getAtoms();
            const atoms2 = res2.getAtoms();
            const cAt = findAtom(atoms1, 'C');
            const oAt = findAtom(atoms1, 'O');
            const nAt = findAtom(atoms2, 'N');
            const hAt = findAtom(atoms2, 'H');

            const amideAtoms = [cAt, oAt, nAt, hAt].filter(
                Boolean
            );
            if (amideAtoms.length < 2) continue;

            // Center at the peptide bond midpoint
            let center = null;
            if (cAt && nAt) {
                center = Div3_1(Add3_3(
                    cAt.getPosition(), nAt.getPosition()
                ), 2);
            }

            addDipole(
                results, res1, amideAtoms,
                'amide', params, center
            );
        }
    }
}

function addDipole(
    results, group, atoms, label, params, centerOverride
) {
    const result = computeDipoleMoment(atoms);
    if (result && result.magnitude >= params.threshold) {
        if (centerOverride) result.centerOfMass = centerOverride;
        results.push({
            group, atoms, label, ...result,
        });
    } else if (group.type === AtomGroupTypes.Cofactor) {
        const charged = atoms.filter((a) => a.charge).length;
        const mag = result ? result.magnitude.toFixed(4) : 'null';
        const sample = atoms.slice(0, 3).map(
            (a) => `${a.atom}:${typeof a.charge}=${a.charge}`
        );
        console.log(
            'Cofactor dipole skipped:', group.resn,
            `${charged}/${atoms.length} charged atoms,`,
            'magnitude:', mag, sample
        );
    }
}

/**
 * Find binding pocket dipole: a single dipole computed from all atoms of the
 * binding site residues. Uses whole residues (not an atom-distance cutoff) so
 * the result is consistent with the visible binding site and responds to the
 * user's Ctrl+mousewheel binding-site radius adjustment.
 * Tagged with `isAggregate: true` for distinct visual styling.
 *
 * @param {import('BMapsModel').AtomGroup[]} compounds - compounds in the binding site
 * @param {import('BMapsModel').AtomGroup[]} residues - binding site residues
 * @param {object} params
 * @returns {Array}
 */
export function findBindingPocketDipole(
    compounds, residues, params = defaultDipoleParams
) {
    if (residues.length === 0 || compounds.length === 0) return [];

    const allResidueAtoms = residues.flatMap((r) => r.getAtoms());
    if (allResidueAtoms.length < 2) return [];

    const geomCenter = computeGeometricCenter(allResidueAtoms);
    const results = [];
    // Use the first compound as the group for tooltip display
    addDipole(results, compounds[0], allResidueAtoms, 'protein', params, geomCenter);

    for (const r of results) r.isAggregate = true;
    return results;
}
